import { useCallback, useEffect, useRef, useState } from "react";

const THRESHOLD = 64;
const MAX_PULL  = 80;

/**
 * usePullToRefresh — native iOS-style pull to refresh.
 * Returns { containerRef, pullProgress, refreshing }.
 * Attach containerRef to the scrollable wrapper.
 * onRefresh may be undefined — pass enabled=false to skip listeners entirely.
 */
export function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const containerRef    = useRef(null);
  const startYRef       = useRef(null);
  const pullProgressRef = useRef(0);
  const refreshingRef   = useRef(false);
  const isMountedRef    = useRef(true);

  const [pullProgress, setPullProgress] = useState(0);
  const [refreshing,   setRefreshing]   = useState(false);

  // Track mount state so trigger never sets state after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const trigger = useCallback(async () => {
    if (refreshingRef.current || !onRefresh) return;
    refreshingRef.current = true;
    pullProgressRef.current = 0;
    setPullProgress(0);
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      refreshingRef.current = false;
      if (isMountedRef.current) setRefreshing(false);
    }
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    let startX = null;
    let directionLocked = false;

    const reset = () => {
      startYRef.current = null;
      startX = null;
      directionLocked = false;
      pullProgressRef.current = 0;
      setPullProgress(0);
    };

    const onTouchStart = (e) => {
      if (refreshingRef.current) return;
      const scrollTop = el.scrollTop ?? window.scrollY;
      if (scrollTop > 4) return;
      const touchY = e.touches[0].clientY;
      if (touchY > window.innerHeight * 0.5) return;
      startYRef.current = touchY;
      startX = e.touches[0].clientX;
      directionLocked = false;
    };

    const onTouchMove = (e) => {
      if (startYRef.current === null || refreshingRef.current) return;
      const deltaY = e.touches[0].clientY - startYRef.current;
      const deltaX = e.touches[0].clientX - startX;

      if (!directionLocked) {
        if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return;
        if (Math.abs(deltaX) >= Math.abs(deltaY)) {
          reset();
          return;
        }
        directionLocked = true;
      }

      if (deltaY <= 0) {
        reset();
        return;
      }
      const progress = Math.min(deltaY, MAX_PULL) / THRESHOLD;
      pullProgressRef.current = progress;
      setPullProgress(progress);
    };

    const onTouchEnd = () => {
      if (startYRef.current === null) return;
      const progress = pullProgressRef.current;
      startYRef.current = null;
      if (progress >= 1) {
        trigger();
      } else {
        pullProgressRef.current = 0;
        setPullProgress(0);
      }
    };

    // touchcancel fires on iOS when the OS interrupts the gesture
    // (phone call, system alert, scroll hand-off). Must reset cleanly.
    const onTouchCancel = () => reset();

    el.addEventListener("touchstart",  onTouchStart,  { passive: true });
    el.addEventListener("touchmove",   onTouchMove,   { passive: true });
    el.addEventListener("touchend",    onTouchEnd,    { passive: true });
    el.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return () => {
      el.removeEventListener("touchstart",  onTouchStart);
      el.removeEventListener("touchmove",   onTouchMove);
      el.removeEventListener("touchend",    onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [trigger, enabled]);

  return { containerRef, pullProgress, refreshing };
}
